import { useCallback, useEffect, useReducer, useRef } from 'react';

type TimerId = ReturnType<typeof setTimeout> | undefined;

const LINGER_DELAY = 1000;
const ICON_MARGIN_DELAY = 800;

type GrowDirection = 'left' | 'right';

interface HoverState {
  isExpanded: boolean;
  isLingering: boolean;
  iconMargin: boolean;
  growDirection: GrowDirection;
}

type HoverAction =
  | { type: 'MOUSE_ENTER'; growDirection: GrowDirection }
  | { type: 'MOUSE_LEAVE' }
  | { type: 'SET_LINGERING' }
  | { type: 'CLEAR_ICON_MARGIN' };

const initialState: HoverState = {
  isExpanded: false,
  isLingering: false,
  iconMargin: false,
  growDirection: 'left',
};

function hoverReducer(state: HoverState, action: HoverAction): HoverState {
  switch (action.type) {
    case 'MOUSE_ENTER':
      return { isExpanded: true, isLingering: false, iconMargin: true, growDirection: action.growDirection };
    case 'MOUSE_LEAVE':
      return { ...state, isExpanded: false, isLingering: false };
    case 'SET_LINGERING':
      return { ...state, isLingering: true };
    case 'CLEAR_ICON_MARGIN':
      return { ...state, iconMargin: false };
  }
}

interface UseAIButtonHoverReturn {
  isExpanded: boolean;
  isLingering: boolean;
  iconMargin: boolean;
  growDirection: GrowDirection;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

const useAIButtonHover = (
  isDragging: boolean,
  elementRef: React.RefObject<HTMLElement | null>,
): UseAIButtonHoverReturn => {
  const [state, dispatch] = useReducer(hoverReducer, initialState);

  const isDraggingRef = useRef(isDragging);
  const prevIsDraggingRef = useRef(isDragging);
  isDraggingRef.current = isDragging;
  const isPointerDownRef = useRef(false);

  const lingerTimerRef = useRef<TimerId>(undefined);
  const iconMarginTimerRef = useRef<TimerId>(undefined);

  const collapseIfOutside = useCallback(() => {
    const el = elementRef.current;
    if (el && !el.matches(':hover')) {
      dispatch({ type: 'MOUSE_LEAVE' });
      clearTimeout(iconMarginTimerRef.current);
      iconMarginTimerRef.current = setTimeout(() => dispatch({ type: 'CLEAR_ICON_MARGIN' }), ICON_MARGIN_DELAY);
      clearTimeout(lingerTimerRef.current);
      lingerTimerRef.current = undefined;
    }
  }, [elementRef]);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    const onPointerDown = () => {
      isPointerDownRef.current = true;
    };
    const onPointerUp = () => {
      if (!isPointerDownRef.current) return;
      isPointerDownRef.current = false;
      if (!isDraggingRef.current) collapseIfOutside();
    };

    el.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [elementRef, collapseIfOutside]);

  const handleMouseEnter = useCallback(() => {
    if (isDraggingRef.current) return;

    let growDirection: GrowDirection = 'left';
    const el = elementRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      if (centerX <= window.innerWidth / 2) {
        growDirection = 'right';
      }
    }

    dispatch({ type: 'MOUSE_ENTER', growDirection });
    clearTimeout(iconMarginTimerRef.current);
    iconMarginTimerRef.current = undefined;
    clearTimeout(lingerTimerRef.current);
    lingerTimerRef.current = setTimeout(() => dispatch({ type: 'SET_LINGERING' }), LINGER_DELAY);
  }, [elementRef]);

  const handleMouseLeave = useCallback(() => {
    if (isDraggingRef.current || isPointerDownRef.current) return;
    dispatch({ type: 'MOUSE_LEAVE' });
    clearTimeout(iconMarginTimerRef.current);
    iconMarginTimerRef.current = setTimeout(() => dispatch({ type: 'CLEAR_ICON_MARGIN' }), ICON_MARGIN_DELAY);
    clearTimeout(lingerTimerRef.current);
    lingerTimerRef.current = undefined;
  }, []);

  useEffect(() => {
    const wasDragging = prevIsDraggingRef.current;
    prevIsDraggingRef.current = isDragging;

    if (isDragging) {
      clearTimeout(lingerTimerRef.current);
      lingerTimerRef.current = undefined;
      clearTimeout(iconMarginTimerRef.current);
      iconMarginTimerRef.current = undefined;
    } else if (wasDragging) {
      collapseIfOutside();
    }
  }, [isDragging, collapseIfOutside]);

  useEffect(() => {
    return () => {
      clearTimeout(lingerTimerRef.current);
      clearTimeout(iconMarginTimerRef.current);
    };
  }, []);

  return {
    isExpanded: state.isExpanded,
    isLingering: state.isLingering,
    iconMargin: state.iconMargin,
    growDirection: state.growDirection,
    handleMouseEnter,
    handleMouseLeave,
  };
};

export default useAIButtonHover;
